<template>
  <div>
    <v-overlay :value="isProcessing">
      <v-progress-circular
        indeterminate
        color="primary"
        :size="70"
        :width="7"
      />
    </v-overlay>
    <VerifyCodeDialog
      :verificationType="emailVerificationType"
      :contactInfo="targetEmail"
      :showDialog="showVerifyEmail"
      :onClose="() => showVerifyEmail = false"
      :onSuccess="() => { showVerifyEmail = false; verifiedEmail = true; }"
    />
    <VerifyCodeDialog
      :verificationType="phoneVerificationType"
      :contactInfo="targetSMS"
      :showDialog="showVerifySMS"
      :onClose="() => showVerifySMS = false"
      :onSuccess="() => { showVerifySMS = false; verifiedSMS = true; }"
    />
    <v-row justify="center" style="margin-bottom: 5rem">
      <v-col class="agreements" lg="6" md="6" sm="10">
        <div class="top-icons">
          <a
            class="back-icon"
            v-on:click="goBack"
          >
            <font-awesome-icon icon="long-arrow-alt-left" size="lg" /> Back
          </a>
        </div>
        <h1 id="title">Agreements</h1>
        <v-col>
          <h2>ASCA Privacy & Data Sharing</h2>
          <p>Please read the following Privacy Statements to continue creating your user account.</p>
          <div>
            <input type="checkbox" id="asca-privacy-policy" v-model="ascaPrivacyPolicy" />
            <label for="asca-privacy-policy"> I agree to the <a href="https://asca.org/privacy-policy/" target="_blank" rel="noreferrer">ASCA Privacy Policy</a></label>
          </div>
          <div>
            <input type="checkbox" id="asca-data-sharing-policy" v-model="ascaDataSharingPolicy" />
            <label for="asca-data-sharing-policy"> I agree to the <a href="https://asca.org/asca-data-collection-and-sharing-policy/" target="_blank" rel="noreferrer">ASCA Data Sharing Policy</a></label>
          </div>
        </v-col>
        <v-col>
          <h2>Communication Method</h2>
          <p>From time-to-time you will be included in ASCA-related business, such as membership renewals, dog or puppy updates or other registration matters. How would you like to be notified about updates, changes and interactions related to your ASCA account? Please choose at least one communication channel to continue.</p>
          <p>After verifying communication methods, please check the one you would prefer as your communication channel to continue with the login process.</p>
          <p v-if="agreementFor === 'VerifyJunior'">The communication method(s) below will be for your Junior and the verification code(s) will go to their account. Please work with them to verify.</p>
          <div class="d-flex">
            <div class="checktext-container">
              <label for="use-email"> Email</label>
            </div>
            <div class="verify-container">
              <p v-if="verifiedEmail">Verified</p>
              <a v-else v-on:click="() => showVerifyEmail = true">Verify now</a>
            </div>
            <div class="checkbox-container">
              <input type="checkbox" id="use-email" v-model="useEmail" :disabled="!verifiedEmail" />
            </div>
          </div>
          <div class="d-flex">
            <div class="checktext-container">
              <label for="use-sms"> SMS/Text</label>
            </div>
            <div class="verify-container">
              <p v-if="verifiedSMS">Verified</p>
              <a v-else v-on:click="() => showVerifySMS = true">Verify now</a>
            </div>
            <div class="checkbox-container">
              <input type="checkbox" id="use-sms" v-model="useSMS" :disabled="!verifiedSMS" />
            </div>
          </div>
        </v-col>
        <v-btn
          :dark="validAgreements()"
          large
          color="primary"
          v-on:click="submit()"
          :disabled="!validAgreements()"
        >SUBMIT</v-btn>
        <v-alert class="mt-3" type="error" v-if="isError">{{ errorMessage }}</v-alert>
      </v-col>
    </v-row>    
    <v-row>
      <v-footer app color="#FAFAFA" class="footer-container">
          <v-row>
            <v-col>
              <v-img height="2rem" contain :src="logoImage" />
              <footer-text />
            </v-col>
          </v-row>
        </v-footer>
    </v-row>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import axios from "axios";
import FooterText from '@/components/common/FooterText.vue';
import VerifyCodeDialog from '@/components/common/VerifyCodeDialog.vue';

import userService from "@/services/portal/userService";
import authenticationService from "@/services/authenticationService";
import { VerificationType } from "@/services/codeVerificationService";

import { UserInfo } from "@/types/User";
import { getErrorMessage } from '@/services/errorHandler';

const logoImage = require('@/assets/logo.svg');

export default Vue.extend({
  components: { FooterText, VerifyCodeDialog },
  name: 'Agreements',
  data() {
    return {
      logoImage,
      isError: false,
      isProcessing: false,
      errorMessage: '',

      showVerifyEmail: false,
      verifiedEmail: false,
      showVerifySMS: false,
      verifiedSMS: false,

      ascaPrivacyPolicy: false,
      ascaDataSharingPolicy: false,
      useEmail: false,
      useSMS: false,
    }
  },
  watch: {
    userInfo: function (newValue) {
      if (!!newValue) {
        this.verifiedEmail = newValue.verifiedEmail;
        this.verifiedSMS = newValue.verifiedSMS;
      }
    }
  },
  computed: {
    agreementFor(): string {
      return this.$store.state.agreementFor;
    },
    userInfo(): UserInfo {
      return this.$store.state.userInfo;
    },
    targetEmail(): string {
      const agreementFor = this.agreementFor;

      switch (agreementFor) {
        case "VerifyJunior":
          const parentVerification = this.$store.state.parentVerification;
          return parentVerification.email;
        case "NewAccount":
        case "ExistingAccount":
          const userInfo = this.userInfo;
          return userInfo?.email;
        default:
          return "";
      }
    },
    targetSMS(): string {
      const agreementFor = this.agreementFor;

      switch (agreementFor) {
        case "VerifyJunior":
          const parentVerification = this.$store.state.parentVerification;
          return parentVerification.phone;
        case "NewAccount":
        case "ExistingAccount":
          const userInfo = this.userInfo;
          return userInfo?.phone;
        default:
          return "";
      }
    },
    emailVerificationType() {
      return VerificationType.Email;
    },
    phoneVerificationType() {
      return VerificationType.PhoneNumber;
    }
  },
  methods: {
    goBack() {
      // User hasn't agreed keep them out till they do
      if (!!this.userInfo) {
        axios.defaults.headers.common["Authorization"] = null;
        localStorage.removeItem("userToken");
        this.$store.commit("loadUserInfo", null);
      }

      this.$router.back();
    },
    verifyJunior: async function() {
      const parentVerification = this.$store.state.parentVerification;

      try {
        const response = await authenticationService.activateJunior({
          juniorId: parseInt(parentVerification.juniorId),
          parentId: parentVerification.parentId ? parseInt(parentVerification.parentId) : null,
          verifiedEmail: this.verifiedEmail,
          verifiedSMS: this.verifiedSMS,
          useEmail: this.useEmail,
          useSMS: this.useSMS,
          agreedToPrivacyPolicy: this.ascaPrivacyPolicy,
          agreedToDataSharingPolicy: this.ascaDataSharingPolicy,
        });

        if (response.errors != null) {
          this.isError = true;
          this.errorMessage = response.errors.join(', ');
          return;
        }

        this.$router.push('/verification/success');
      } catch (error) {
        this.isError = true;
        this.errorMessage = getErrorMessage(error);
      } finally {
        this.isProcessing = false;
      }
    },
    saveAgreements: async function() {
      await userService.setUserAgreements({
        verifiedEmail: this.verifiedEmail,
        verifiedSMS: this.verifiedSMS,
        useEmail: this.useEmail,
        useSMS: this.useSMS,
        agreedToPrivacyPolicy: this.ascaPrivacyPolicy,
        agreedToDataSharingPolicy: this.ascaDataSharingPolicy,
      });

      const newUserInfo = await userService.getProfile();

      this.$store.commit('loadUserInfo', newUserInfo);

      return newUserInfo;
    },
    setAgrementsForAccount: async function(newAccount: boolean) {
      const newUserInfo = await this.saveAgreements();

      this.isProcessing = false;

      if (newAccount) this.$router.push('/signup/success');
      else if (newUserInfo.hasNeverBeenMember) this.$router.push('/gain-membership');
      else this.$router.push(newUserInfo.isActiveMember ? '/profile' : '/expired-membership');
    },
    submit: function() {
      this.isProcessing = true;

      const agreementFor = this.agreementFor;

      switch (agreementFor) {
        case "VerifyJunior":
          return this.verifyJunior();
        case "NewAccount":
        case "ExistingAccount":
          return this.setAgrementsForAccount(agreementFor === "NewAccount");
      }
    },
    validAgreements() {
      return !!this.ascaDataSharingPolicy
        && !!this.ascaPrivacyPolicy
        && (this.useEmail || this.useSMS);
    },
  },
});
</script>
